import React from 'react';
import { Link } from 'react-router-dom';

interface CtaButtonBaseProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'outline' | 'slate';
}

interface CtaButtonLinkProps extends CtaButtonBaseProps {
  as?: 'a';
  href: string;
  onClick?: () => void;
}

interface CtaButtonRouterProps extends CtaButtonBaseProps {
  as: 'link';
  to: string;
}

interface CtaButtonSubmitProps extends CtaButtonBaseProps {
  as: 'button';
  type: 'submit';
  disabled?: boolean;
}

interface CtaButtonClickProps extends CtaButtonBaseProps {
  as: 'button';
  type?: 'button';
  disabled?: boolean;
  onClick?: () => void;
}

type CtaButtonProps =
  | CtaButtonLinkProps
  | CtaButtonRouterProps
  | CtaButtonSubmitProps
  | CtaButtonClickProps;

const baseStyles =
  'px-8 py-4 rounded-full font-bold text-lg transition active:scale-95 duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:scale-[1.03] hover:-translate-y-0.5 hover:shadow-2xl flex items-center justify-center';

const variantStyles = {
  primary: 'bg-primary text-white border border-transparent hover:brightness-110',
  outline: 'bg-white text-slate-700 border border-slate-200 hover:brightness-105',
  slate: 'bg-slate-800 text-white border border-slate-700 hover:brightness-110',
};

const disabledStyles =
  'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0 disabled:hover:shadow-none';

// eslint-disable-next-line import/prefer-default-export
export function CtaButton(props: CtaButtonProps) {
  const { children, className = '', variant = 'primary', as: renderAs } = props;
  const classes = `${baseStyles} ${variantStyles[variant]} ${className}`;

  if (renderAs === 'button') {
    const { type = 'button', disabled } = props as CtaButtonSubmitProps | CtaButtonClickProps;
    const onClick = 'onClick' in props ? props.onClick : undefined;

    if (type === 'submit') {
      return (
        <button type="submit" disabled={disabled} className={`${classes} ${disabledStyles}`}>
          {children}
        </button>
      );
    }

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`${classes} ${disabledStyles}`}
      >
        {children}
      </button>
    );
  }

  if (renderAs === 'link') {
    const { to } = props as CtaButtonRouterProps;
    return (
      <Link to={to} className={classes}>
        {children}
      </Link>
    );
  }

  // Default: <a> tag (backward compatible)
  const { href, onClick } = props as CtaButtonLinkProps;
  return (
    <a href={href} onClick={onClick} className={classes}>
      {children}
    </a>
  );
}
